import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import api from '../../../services/api';
import { useToast } from '../../../hooks/toast';
import SecondaryButton from '../../../components/Buttons/SecondaryButton';
import { StepTeams } from '../CreateTournament/steps/StepTeams';
import { RowActions } from '../../../components/Tournament';
import {
  CATEGORY_NUMBER_TO_LABEL,
  ITournamentModality,
  ITournamentSetupArena,
  ITournamentSetupData,
  MODALITY_SLUG_TO_NAME,
} from '../CreateTournament';
import {
  AddUserIcon,
  ArrowBackIcon,
  TrashIcon,
} from '../../../assets/svg/SVGComponents';
import {
  ConfirmActions,
  ConfirmDialog,
  ConfirmOverlay,
  ConfirmText,
  ConfirmTitle,
  ContentLayout,
  DangerButton,
  EmptyState,
  FormColumn,
  ListColumn,
  ListHeader,
  LoadingWrapper,
  MemberChip,
  MembersList,
  PageHeader,
  PageTitle,
  PageWrapper,
  Spinner,
  TeamCard,
  TeamInfo,
  TeamMeta,
  TeamName,
} from './styles';

interface ITeamMember {
  id: string;
  student_name: string;
  school_year: number;
}

interface ITeam {
  id: string;
  name: string;
  category: string;
  tournament_modality_id: string;
  tournament_modality?: {
    id: string;
    modality: string;
  };
  members?: ITeamMember[];
}

interface IApiTournament {
  id: string;
  name: string;
  date?: string | null;
  end_date?: string | null;
  submission_deadline?: string | null;
  max_teams_per_school?: string | null;
  status?: string;
  stage?: string;
  location?: string | null;
  school_id?: string;
  season_id?: string;
  school?: ITournamentSetupData['school'];
  modalities?: Array<{
    id: string;
    modality: string;
    max_rounds?: number;
    time_limit_seconds?: number;
    created_at?: string;
    arenas?: ITournamentModality['arenas'];
  }>;
  arenas?: ITournamentSetupArena[];
}

const TournamentTeams: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [tournamentData, setTournamentData] =
    useState<ITournamentSetupData | null>(null);
  const [teams, setTeams] = useState<ITeam[]>([]);
  const [teamToDelete, setTeamToDelete] = useState<ITeam | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);

    try {
      const [tournamentResult, teamsResult, setupResult] =
        await Promise.allSettled([
          api.get<IApiTournament>(`/tournaments/${id}`),
          api.get<ITeam[]>(`/tournaments/${id}/teams`),
          api.get('/tournaments/setup/frl-2026'),
        ]);

      if (tournamentResult.status !== 'fulfilled') {
        throw new Error('tournament_fetch_failed');
      }

      const tournament = tournamentResult.value.data;
      const teamsData =
        teamsResult.status === 'fulfilled' ? teamsResult.value.data : [];
      const setup =
        setupResult.status === 'fulfilled' ? setupResult.value.data : undefined;

      const modalities: ITournamentModality[] = (
        tournament.modalities ?? []
      ).map(m => ({
        id: m.id,
        modality: m.modality,
        max_rounds: m.max_rounds,
        time_limit_seconds: m.time_limit_seconds,
        created_at: m.created_at,
        arenas: m.arenas ?? [],
      }));

      const rawArenas = tournament.arenas ?? setup?.arenas ?? [];
      const setupArenas: ITournamentSetupArena[] = rawArenas.map(
        (a: ITournamentSetupArena) => ({
          name: a.name,
          modality: a.modality,
          categories: a.categories ?? [],
        }),
      );

      const fallbackArenas: ITournamentSetupArena[] = modalities.flatMap(m =>
        m.arenas.map(a => ({
          name: a.name,
          modality: m.modality,
          categories: [],
        })),
      );

      setTournamentData({
        id: tournament.id,
        name: tournament.name,
        date: tournament.date ?? null,
        end_date: tournament.end_date ?? null,
        submission_deadline: tournament.submission_deadline ?? null,
        max_teams_per_school: tournament.max_teams_per_school ?? null,
        status: tournament.status ?? '',
        stage: tournament.stage ?? '',
        location: tournament.location ?? null,
        school: tournament.school ?? setup?.school,
        school_id:
          tournament.school_id ?? setup?.school?.id ?? setup?.school_id ?? '',
        season_id: tournament.season_id ?? setup?.season_id ?? '',
        arenas: setupArenas.length ? setupArenas : fallbackArenas,
        modalities,
      });

      setTeams(Array.isArray(teamsData) ? teamsData : []);
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Não foi possível carregar os dados do torneio.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [addToast, id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDeleteConfirm = async () => {
    if (!teamToDelete || !id) return;
    setIsDeleting(true);
    try {
      await api.delete(`/tournaments/${id}/teams/${teamToDelete.id}`);
      setTeams(prev => prev.filter(t => t.id !== teamToDelete.id));
      addToast({
        type: 'success',
        expression: 'Removido!',
        description: 'Equipe removida com sucesso.',
      });
      setTeamToDelete(null);
    } catch {
      addToast({
        type: 'error',
        expression: 'Erro!',
        description: 'Não foi possível remover a equipe.',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const getModalityLabel = (team: ITeam): string => {
    const slug = team.tournament_modality?.modality ?? '';
    return (
      MODALITY_SLUG_TO_NAME[slug as keyof typeof MODALITY_SLUG_TO_NAME] ??
      slug ??
      '-'
    );
  };

  const getCategoryLabel = (category: string): string => {
    const num = parseInt(category.replace('category_', ''), 10);
    return CATEGORY_NUMBER_TO_LABEL[num] ?? category ?? '-';
  };

  if (isLoading) {
    return (
      <PageWrapper>
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <PageHeader>
        <Link to={-1 as any}>
          <ArrowBackIcon
            width="1.33rem"
            stroke="#1E88E5"
            strokeWidth={3}
            fill="none"
          />
          Voltar
        </Link>
        <div>
          <AddUserIcon
            width="1.6rem"
            stroke="#FF9800"
            strokeWidth={2.5}
            fill="none"
          />
          <PageTitle>Equipes do torneio</PageTitle>
        </div>
      </PageHeader>

      <ContentLayout>
        <FormColumn>
          {tournamentData && (
            <StepTeams
              title="Nova equipe"
              tournamentData={tournamentData}
              onSaveSuccess={loadData}
            />
          )}
        </FormColumn>

        <ListColumn>
          <ListHeader>
            <h2>Equipes cadastradas ({teams.length})</h2>
          </ListHeader>

          {teams.length === 0 ? (
            <EmptyState>Nenhuma equipe cadastrada ainda.</EmptyState>
          ) : (
            teams.map(team => (
              <TeamCard key={team.id}>
                <TeamInfo>
                  <TeamName>{team.name}</TeamName>
                  <TeamMeta>
                    {[getModalityLabel(team), getCategoryLabel(team.category)]
                      .filter(part => part && part !== '-')
                      .join(' · ')}
                  </TeamMeta>
                  {(team.members?.length ?? 0) > 0 && (
                    <MembersList>
                      {team.members!.map(m => (
                        <MemberChip key={m.id}>
                          {m.student_name} ({m.school_year}º ano)
                        </MemberChip>
                      ))}
                    </MembersList>
                  )}
                </TeamInfo>
                <RowActions
                  label={`Ações de ${team.name}`}
                  actions={[
                    {
                      label: 'Remover equipe…',
                      destructive: true,
                      onSelect: () => setTeamToDelete(team),
                    },
                  ]}
                />
              </TeamCard>
            ))
          )}
        </ListColumn>
      </ContentLayout>

      {teamToDelete && (
        <ConfirmOverlay onClick={() => !isDeleting && setTeamToDelete(null)}>
          <ConfirmDialog onClick={e => e.stopPropagation()}>
            <ConfirmTitle>Remover equipe</ConfirmTitle>
            <ConfirmText>
              Tem certeza que deseja remover a equipe{' '}
              <strong>{teamToDelete.name}</strong>? Esta ação não pode ser
              desfeita.
            </ConfirmText>
            <ConfirmActions>
              <SecondaryButton
                type="button"
                onClick={() => setTeamToDelete(null)}
                disabled={isDeleting}
              >
                Cancelar
              </SecondaryButton>
              <DangerButton
                type="button"
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                <TrashIcon
                  width="0.85rem"
                  stroke="currentColor"
                  strokeWidth={2}
                  fill="none"
                />
                {isDeleting ? 'Removendo...' : 'Remover equipe'}
              </DangerButton>
            </ConfirmActions>
          </ConfirmDialog>
        </ConfirmOverlay>
      )}
    </PageWrapper>
  );
};

export default TournamentTeams;
